import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useLoggedUserContext } from '../../AppContext';
import gameClient from '../../infrastructure/gameClient';
import UIError from "../UIError";
import ErrorPopup from "../error/ErrorPopup";
import GamePanel from "./GamePanel";
import { Link } from 'react-router-dom';
import { useState } from "react";
import "./multiGamesPanel.css";

export default function MultiGamesPanel() {
    const navigate = useNavigate();
    const setLoggedUser = useLoggedUserContext()[1];
    const [reload, setReload] = useState(0);
    const [view, setView] = useState("DEFAULT");

    const gamesQuery = useQuery({
        queryKey: ["games", view, reload],
        queryFn: async () => await fetchGames(view),
    });

    const refresh = () => !gamesQuery.isPending && setReload(reload + 1);
    const updateView = view => {
        setView(view);
        setReload(reload + 1);
    }

    return <div className="popup multiGamesPanel">
        <div className="title"><img alt="Kingds and Emperors" src="logo.png"/> {getTitle(view, gamesQuery)}</div>
        <div className="separator"/>
        <div className='content'>
            <div className="scrollBox">
                {gamesQuery.isError && renderError(gamesQuery.error, setLoggedUser, navigate)}
                {gamesQuery.isPending && renderLoading()}
                {gamesQuery.isSuccess && renderGames(gamesQuery.data)}
            </div>
            {renderButtons(gamesQuery, view, refresh, updateView)}
        </div>
    </div>
}

function renderLoading() {
    return <div className="loading"><img alt="loading" src="./loading-transparent.gif"/></div>
}

function renderButtons(gamesQuery, view, refresh, updateView) {
    return <div className="buttons">
        {renderSwitchViewButton(gamesQuery, view, updateView)}
        <button disabled={gamesQuery.isPending} onClick={refresh}>Reload</button>
        {gamesQuery.isPending ? <button disabled>Back</button> : <Link to="/"><button>Back</button></Link>}
    </div>
}

function renderSwitchViewButton(gamesQuery, view, updateView) {
    if (gamesQuery.isSuccess) {
        return renderSwitchViewButtonByType(gamesQuery.data.type, updateView, false);
    }

    if (view === "NEW") {
        return renderSwitchViewButtonByType("NEW", updateView, true);
    }
    else {
        return renderSwitchViewButtonByType("STARTED", updateView, true);
    }
}

function renderSwitchViewButtonByType(type, updateView, disabled) {
    if (type==="STARTED") {
        return <button disabled={disabled} onClick={() => updateView("NEW")}>New game</button>;
    }

    return <button disabled={disabled} onClick={() => updateView("STARTED")}>Started games</button>;
}

function getTitle(view, gamesQuery) {
    if (view === "DEFAULT") {
        if (gamesQuery.isSuccess) {
            return getTitleByType(gamesQuery.data.type);
        }
        return getTitleByType("STARTED");
    }

    return getTitleByType(view);
}

function getTitleByType(type) {
    if (type === "STARTED") {
        return "Started multiplayer games";
    }

    return "Join new multiplayer game";
}

function renderGames(data) {
    renderWarnings(data.servers);
    const games = data.servers.flatMap(gs => gs.games.map(g => {
        return {
            game: g,
            server: gs.server,
        }
    }));
    games.sort((l, r) => l.game.created - r.game.created);

    if (games.length > 0) {
        return <div>
            {games.map(g => <GamePanel key={g.game.id} game={g.game} server={g.server}/>)}
            <br style={{clear: "both"}}/>
        </div>
    }
    else {
        return <div className="empty">No games found</div>
    }
}

function renderWarnings(gameServers) {
    let allFiled = true;
    let allSucces = true;
    for (const gameServer of gameServers) {
        if (gameServer.game) {
            allFiled = false;
        }
        else {
            allSucces = false;
        }
    }

    if (allFiled) {
        return <div>Failed to load games!</div>
    }

    if (!allSucces) {
        return <div>Some games might be missing</div>
    }

    return null;
}

function renderError(error, setLoggedUser, navigate) {
    if (error.name === "UIError" && error.message === "UNAUTHORIZED") {
        setTimeout(() => {
            setLoggedUser(null);
            navigate('/login');
        });
        return null;
    }

    return <ErrorPopup message={error.message}/>
}

async function fetchGames(view) {
    if (view === "NEW") {
        return await fetchNewGames();
    }
    else if (view === "STARTED") {
        return await fetchStartedGames();
    }
    else {
        const result = await fetchStartedGames();
        if (result.servers.filter(server => server.games.length > 0).length > 0) {
            return result;
        }
        else {
            return await fetchNewGames();
        }
    }
};

async function fetchStartedGames() {
    const res = await gameClient.getStartedGames();
    //await new Promise(r => setTimeout(r, 10000));
    return await fetchResponse(res, "STARTED");
};

async function fetchNewGames() {
    const res = await gameClient.getNewGames();
    return await fetchResponse(res, "NEW");
};

async function fetchResponse(response, type) {
    if (response.ok) {
        const json = await response.json();
        return {
            type: type,
            servers: json,
        }
    }

    if (response.status === 401) {
        throw new UIError("UNAUTHORIZED");
    }

    throw new UIError("Error status code: " + response.status);
}
